import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GetWasteShipmentQuery, WasteShipmentStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";

import { ManifestUploadDialog } from "../Manifests/ManifestUploadDialog";
import {
  ATTACH_SHIPMENT_MANIFEST_MUTATION,
  DETACH_SHIPMENT_MANIFEST_MUTATION,
  MANIFEST_LINK_REFETCH_QUERIES,
} from "./api";
import { AttachManifestDialog } from "./AttachManifestDialog";
import { DetachManifestDialog } from "./DetachManifestDialog";
import { ManifestStepActions } from "./ManifestStepActions";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/**
 * The two mutations that change the shipment's link to a manifest, with their
 * alerts. Both report failure the same way and neither has any bearing on the
 * dialogs' open state, so they sit together and away from it.
 */
function useManifestLinkActions(shipment: Shipment) {
  const alerts = useAlerts();
  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });
  const [detach, { loading: detaching }] = useMutation(DETACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });

  const setSigned = async (signed: boolean) => {
    const manifestId = shipment.manifest?.id;
    if (!manifestId) {
      return;
    }
    try {
      // Re-attaching the manifest already linked is the signed-copy flow; the
      // server keeps the original received date.
      await attach({
        variables: { input: { shipmentId: shipment.id, processedManifestId: manifestId, signed } },
      });
      alerts.success(signed ? "Marked as the signed copy." : "Marked as an unsigned draft.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the manifest.");
    }
  };

  /** Resolves true when the link was actually released, so the caller can close. */
  const detachManifest = async (): Promise<boolean> => {
    try {
      await detach({ variables: { shipmentId: shipment.id } });
      alerts.success("Manifest detached.");
      return true;
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to detach the manifest.");
      return false;
    }
  };

  return { attaching, detaching, setSigned, detachManifest };
}

/**
 * The official manifest for a shipment, and how it compares to what was
 * requested.
 *
 * Attaching is independent of the shipment's own progress — the vendor's copy
 * can arrive before or after the load leaves — so this section is live in every
 * status except canceled, and never gates or is gated by "Mark shipped".
 *
 * A difference here is not an error. The vendor re-weighs at the scale,
 * re-classifies, splits a line; the manifest is the regulated record and the
 * request was an estimate. So the panel reports rather than blocks, and the
 * severity comes from the same scale the manifest discrepancy views use.
 */
export function ShipmentManifestSection({
  shipment,
  attachBlockedReason,
}: {
  shipment: Shipment;
  /**
   * Why a manifest cannot be attached right now, or undefined when it can.
   *
   * Staged container changes are the case: attaching freezes the contents, so
   * the save that was about to commit them would be refused and the edit could
   * only be thrown away. Detaching is deliberately not blocked — it unlocks the
   * contents, so it is the way out rather than a way in.
   */
  attachBlockedReason?: string;
}) {
  const { tenantId } = useTenant();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detachOpen, setDetachOpen] = useState(false);

  const { attaching, detaching, setSigned, detachManifest } = useManifestLinkActions(shipment);

  const reconciliation = shipment.reconciliation;
  const trackingNumber =
    reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;

  const handleDetach = async () => {
    if (await detachManifest()) {
      setDetachOpen(false);
    }
  };

  // A canceled shipment can take no manifest, and its step already renders
  // inactive and greyed with nothing under it — which reads as "not applicable"
  // without a sentence saying so.
  if (shipment.status === WasteShipmentStatus.Canceled) {
    return null;
  }

  return (
    <>
      <ManifestStepActions
        trackingNumber={shipment.manifestReceivedAt ? trackingNumber : null}
        manifestHref={
          tenantId && shipment.manifest
            ? `/o/${tenantId}/waste/manifests/${shipment.manifest.id}`
            : null
        }
        signed={shipment.manifestSigned}
        dangling={shipment.manifestReceivedAt != null && shipment.manifest == null}
        attachBlockedReason={attaching ? "Attaching…" : attachBlockedReason}
        signedChangePending={attaching}
        onAttach={() => {
          setReplacing(false);
          setPickerOpen(true);
        }}
        onRelink={() => {
          setReplacing(true);
          setPickerOpen(true);
        }}
        onDetach={() => setDetachOpen(true)}
        onSignedChange={setSigned}
      />

      {pickerOpen && (
        <AttachManifestDialog
          shipmentId={shipment.id}
          facilityId={shipment.facilityId}
          replacingTrackingNumber={replacing ? trackingNumber : null}
          onClose={() => setPickerOpen(false)}
          // Hand off rather than stack: the picker has served its purpose once
          // the reader knows the manifest is not in it. Replacing is exempt —
          // that swaps in another manifest Encamp already has.
          onUploadInstead={
            replacing
              ? undefined
              : () => {
                  setPickerOpen(false);
                  setUploadOpen(true);
                }
          }
        />
      )}
      {uploadOpen && tenantId && (
        <ManifestUploadDialog
          open
          onClose={() => setUploadOpen(false)}
          tenantId={tenantId}
          facilityId={shipment.facilityId}
        />
      )}
      {detachOpen && (
        <DetachManifestDialog
          trackingNumber={trackingNumber}
          detaching={detaching}
          onConfirm={handleDetach}
          onClose={() => setDetachOpen(false)}
        />
      )}
    </>
  );
}
